/**
 * Follow-up Board — kanban view of the client roster by follow-up tier.
 * Drag a card between columns (or use the card's move dropdown) → asks for a
 * short remark, then persists the tier + remark into the client's edit history.
 */
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, DollarSign, User as UserIcon, GripVertical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SALES_ADMINS } from '../constants/salesAdmins';

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { tier: 0, label: 'Belum Follow Up', color: '#6B7280' },
  { tier: 1, label: 'Tier 1', color: '#3B82F6' },
  { tier: 2, label: 'Tier 2', color: '#F59E0B' },
  { tier: 3, label: 'Tier 3', color: '#EF4444' },
  { tier: 4, label: 'Done', color: '#10B981' },
  { tier: 5, label: 'Not Interested', color: '#94A3B8' },
];

const fmtMYR = (n) => `RM${Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;

const daysLeft = (expiry, todayISO) => {
  if (!expiry) return null;
  return Math.round((new Date(expiry) - new Date(todayISO)) / 86400000);
};

function ClientCard({ row, todayISO, onDragStart, onMove, busy }) {
  const d = daysLeft(row.expiry_date, todayISO);
  const urgent = row.status === 'expired' || (d !== null && d <= 7);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, row)}
      className={`group rounded-lg border p-2.5 cursor-grab active:cursor-grabbing transition-colors bg-[hsl(var(--surface-elevated))] ${urgent ? 'border-[#EF4444]/50 hover:border-[#EF4444]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]'}`}
      data-testid={`board-card-${row.id}`}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical size={11} className="text-[hsl(var(--text-muted))] mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-white truncate">{row.name}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${row.status === 'active' ? 'text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10' : 'text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10'}`}>
              {row.status === 'active' ? 'ACTIVE' : 'EXPIRED'}
            </span>
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded border text-[#8B5CF6] border-[#8B5CF6]/40 bg-[#8B5CF6]/10">
              {row.renew_count || 1}x
            </span>
            {urgent && (
              <span className="inline-flex items-center gap-0.5 text-[8px] font-bold text-[#EF4444]">
                <AlertTriangle size={8} />
                {row.status === 'expired' ? 'EXPIRED' : `${d}d`}
              </span>
            )}
          </div>
          <div className="mt-1.5 space-y-0.5 text-[9px] text-[hsl(var(--text-muted))]">
            <p>Expiry: <span className="text-white">{row.expiry_date || '—'}</span></p>
            <p className="flex items-center gap-1"><DollarSign size={8} /> {fmtMYR(row.deposit_myr)}</p>
            <p className="flex items-center gap-1"><UserIcon size={8} /> {row.closed_by || '—'}</p>
            <p className="flex items-center gap-1 text-[#3B82F6]"><UserIcon size={8} /> FU: {row.follow_up_by || '—'}</p>
            {row.last_follow_up_remark && (
              <p className="italic text-[#F59E0B] truncate" title={row.last_follow_up_remark}>“{row.last_follow_up_remark}”</p>
            )}
          </div>
          <select
            value={Number(row.follow_up_tier || 0)}
            onChange={(e) => onMove(row, Number(e.target.value))}
            disabled={busy}
            className="mt-2 w-full text-[9px] bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-1.5 py-1 text-white outline-none cursor-pointer"
            data-testid={`board-move-${row.id}`}
          >
            {COLUMNS.map((c) => <option key={c.tier} value={c.tier}>{c.label}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function ClientFollowUpBoard({ rows, ibId, todayISO, onChanged }) {
  const [adminFilter, setAdminFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dragOver, setDragOver] = useState(null);
  const [pending, setPending] = useState(null); // { row, tier }
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => rows.filter((r) => {
    if (adminFilter !== 'all' && (r.closed_by || '') !== adminFilter) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  }), [rows, adminFilter, statusFilter]);

  const byTier = useMemo(() => {
    const map = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [] };
    visible.forEach((r) => map[Number(r.follow_up_tier || 0)].push(r));
    Object.values(map).forEach((list) => list.sort((a, b) => (a.expiry_date || '9999').localeCompare(b.expiry_date || '9999')));
    return map;
  }, [visible]);

  const requestMove = (row, tier) => {
    if (Number(row.follow_up_tier || 0) === tier) return;
    setPending({ row, tier });
    setRemark('');
  };

  const confirmMove = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/clients/${ibId}/${pending.row.id}/follow-up-tier`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ follow_up_tier: pending.tier, remark }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${pending.row.name} → ${COLUMNS.find((c) => c.tier === pending.tier).label}`);
      setPending(null);
      setRemark('');
      onChanged?.();
    } catch {
      toast.error('Gagal update follow-up');
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (e, row) => {
    e.dataTransfer.setData('text/plain', row.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e, tier) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const row = rows.find((r) => r.id === id);
    if (row) requestMove(row, tier);
  };

  return (
    <div className="space-y-3" data-testid="followup-board">
      {/* Board filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={adminFilter} onValueChange={setAdminFilter}>
          <SelectTrigger className="w-[150px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="board-admin-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sales Admin</SelectItem>
            {[...new Set([...SALES_ADMINS, ...rows.map((r) => r.closed_by).filter(Boolean)])].map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-8 text-[10px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="board-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Access Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-[hsl(var(--text-muted))]" data-testid="board-count">
          {visible.length} client · drag card antara lajur untuk update follow-up
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
        {COLUMNS.map((col) => (
          <div
            key={col.tier}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.tier); }}
            onDragLeave={() => setDragOver((t) => (t === col.tier ? null : t))}
            onDrop={(e) => onDrop(e, col.tier)}
            className={`rounded-xl border p-2.5 min-h-[220px] transition-colors ${dragOver === col.tier ? 'border-[#E63946] bg-[#E63946]/5' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))]'}`}
            data-testid={`board-column-${col.tier}`}
          >
            <div className="flex items-center justify-between mb-2.5 px-1">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-white">{col.label}</span>
              </div>
              <span className="text-[10px] font-black font-mono" style={{ color: col.color }} data-testid={`board-count-${col.tier}`}>
                {byTier[col.tier].length}
              </span>
            </div>
            <div className="space-y-2">
              {byTier[col.tier].length === 0 && (
                <p className="text-[10px] text-[hsl(var(--text-muted))] italic px-1 py-6 text-center">Tiada client</p>
              )}
              {byTier[col.tier].map((r) => (
                <ClientCard key={r.id} row={r} todayISO={todayISO} onDragStart={onDragStart} onMove={requestMove} busy={saving} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Remark prompt */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v && !saving) { setPending(null); setRemark(''); } }}>
        <DialogContent className="sm:max-w-sm bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" data-testid="board-remark-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {pending?.row?.name} → {COLUMNS.find((c) => c.tier === pending?.tier)?.label}
            </DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))] tracking-wider">Remark follow-up</label>
            <Input
              autoFocus
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmMove(); }}
              placeholder="cth: call, tak jawab"
              className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"
              data-testid="board-remark-input"
            />
            <p className="text-[9px] text-[hsl(var(--text-muted))] mt-1">Remark masuk ke edit history client.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-[hsl(var(--text-muted))]" onClick={() => { setPending(null); setRemark(''); }} data-testid="board-remark-cancel">Cancel</Button>
            <Button className="bg-[#E63946] hover:bg-[#d32f3f] text-white" onClick={confirmMove} disabled={saving} data-testid="board-remark-save">
              {saving && <Loader2 size={13} className="animate-spin mr-1.5" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
